/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {Log} from "../utils/Log";
import {SecurityAgent} from "../e2ee/SecurityAgent.js";
import {HubInfo} from "./HubInfo.js";
import {Actor} from "./Actor.js";
import {Utils} from "../utils/Utils";
import {NoEncoding} from "./ExecutionMessage.js";


/**
 * SSE transport backed by a REST send path.
 *
 * SSE is receive-only, so outgoing messages are serialized through a client-side queue
 * to preserve ordering and to make send failures observable by upper RPC layers.
 */
export class EventSourceActor extends Actor {
    /**
     * @type {EventSource|null}
     */
    sse = null;

    ////////////////////////////////////////
    //  Static and designated constructors
    //////////////////////////////////////

    /**
     * @param {string} hubName
     * @param {string} hubCode
     * @param {string} aesKey
     * @param {string} actorName
     * @param {string} actorActivation
     * @param {string|undefined} host
     * @return {Promise<EventSourceActor>}
     */
    static async createHubAndActor(hubName, hubCode, aesKey, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID(), host = HubInfo.defaultHost()) {
        const hubInfos = await HubInfo.createHub(hubName, hubCode, aesKey, host);
        return EventSourceActor.connected(hubInfos, actorName, actorActivation);
    }

    /**
     * @param {string} hubName
     * @param {string} hubCode
     * @param {string} actorName
     * @param {string} actorActivation
     * @param {string|undefined} host
     * @return {Promise<EventSourceActor>}
     */
    static async createActor(hubName, hubCode, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID(), host = HubInfo.defaultHost()) {
        const hubInfos = await HubInfo.get(hubName, hubCode, host);
        return EventSourceActor.connected(hubInfos, actorName, actorActivation);
    }

    /**
     * @param {HubInfo} hubInfos
     * @param {string} actorName
     * @param {string} actorActivation
     * @return {Promise<EventSourceActor>}
     * @constructor
     */
    static async connected(hubInfos, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID()) {
        if (!hubInfos) {
            throw new Error("[EventSourceActor.Connected] hub is invalid")
        }
        const actor = new EventSourceActor(hubInfos, actorName, actorActivation);
        const hubUrl = `${actor.httpHost}/api/v1/actors/sse/subscribe/to/${hubInfos.name}`;
        await actor.connect(hubUrl);
        return Promise.resolve(actor);
    }

    /**
     * @param {HubInfo} hubInfos
     * @param {string} actorName
     * @param {string} actorActivation
     * @constructor
     */
    constructor(hubInfos, actorName = Utils.v4UUID(), actorActivation = Utils.v4UUID()) {
        super(hubInfos, actorName, actorActivation);
        this.httpHost = SecurityAgent.apiHostFrom(hubInfos.host);
        this.calls = {};
        this.name = actorName;
        this.activation = actorActivation;
        this._initSSEMessageQueue();
    }


    ////////////////////////////////////////
    //  Connection / Disconnection
    ////////////////////////////////////////

    /**
     * @param hubUrl
     * @return {Promise<unknown>}
     */
    async connectTo(hubUrl) {
        return new Promise((resolve, _) => {
            try {
                this.sse = new EventSource(hubUrl);
            } catch (e) {
                Log.error(`[EventSourceActor.connectTo] Error creating EventSource: ${e.message}`);
                resolve(false);
                return;
            }

            this.sse.onmessage = async (event) => {
                try{
                    await this.handleIncomingMessage(event.data);
                }catch (e){
                    Log.error("[EventSourceActor.connectTo.sse.onmessage] exception: ", e)
                }
            };

            this.sse.onopen = (_) => {
                Log.info('[EventSourceActor.connectTo.sse.onopen] SSE connection established ', this.hubInfo.name, this.name ? `as ${this.name}` : '');
                // Reset reconnection attempts on successful connection to avoid pathological backoff after transient network events.
                this._reconnectAttempts = 0;
                this._reconnectDelay = 1000;
                this.connected = true;
                resolve(true);
            };

            this.sse.onerror = (errorEvent) => {
                const errorMessage = errorEvent.message || "Unknown SSE error";
                const errorType = errorEvent.type || "error";
                Log.error(`[EventSourceActor.connectTo.see.onerror] SSE error: Type - ${errorType}, Message - ${errorMessage}`, errorEvent);
                if (!this.connected) {
                    resolve(false);
                }
            };

            this.sse.onclose = async () => {
                Log.info('[EventSourceActor.connectTo.see.onclose] SSE connection closed ', this.hubInfo.name);
                if (!this.explicitlyDisconnected) {
                    await this.connectionAttempt(hubUrl, true);
                } else {
                    this.sse = null;
                }
            };

            this.sse.addEventListener('open', () => {
                Log.info(`[EventSourceActor.connectSSE] EventSource readyState: ${this.sse.readyState}`);
            });

            this.sse.addEventListener('error', () => {
                Log.info(`[EventSourceActor.connectSSE] EventSource readyState: ${this.sse.readyState}`);
            });
        });
    }


    async disconnect() {
        await super.disconnect();
        if (this.sse) {
            this.sse.close();
            this.sse = null;
            this.stopSSEMessageQueue();
        }
        return Promise.resolve();
    }

    /**
     * SSE cannot send frames, so outbound messages are posted via REST.
     *
     * IMPORTANT:
     * - The send function MUST return a Promise that resolves/rejects based on the actual REST outcome
     *   so upstream RPC helpers can surface local-send failures consistently across transports.
     *
     * @param {ExecutionMessage} message
     * @param {string} [encoding=NoEncoding]
     * @param {number} [timeOut=5000]
     * @returns {Promise<void>}
     */
    sendExecutionMessage(message, encoding=NoEncoding, timeOut=5000) {
        return message.transform((textualMessage)=>{
            return this._addMessageToSSEMessageQueue(textualMessage);
        }, encoding, timeOut);
    }


    ////////////////////////////////////////
    //  SSE message Queue.
    ////////////////////////////////////////


    /**
     * @private
     */
    _initSSEMessageQueue() {
        this._sseMessageQueue = [];
        this._stopSSEMessageQueue = false;
        this._sseRestMessageProgress = false;
        this._handleMessageQueue();
    }

    stopSSEMessageQueue() {
        this._stopSSEMessageQueue = true;
    }

    /**
     * Queue entries carry resolve/reject so send errors propagate to ExecutionMessage._send and then to call/callPromise.
     *
     * @param {string} textualMessage
     * @returns {Promise<void>}
     * @private
     */
    _addMessageToSSEMessageQueue(textualMessage) {
        if (this._stopSSEMessageQueue) {
            return Promise.reject(new Error("SSE message queue is stopped"));
        }
        return new Promise((resolve, reject) => {
            this._sseMessageQueue.push({message: textualMessage, resolve, reject});
        });
    }

    /**
     * Serializes REST sends to preserve execution order (REST does not guarantee in-order delivery under concurrency).
     * Uses an async yield loop to avoid blocking the main thread.
     * @private
     */
    _handleMessageQueue() {
        if (this._stopSSEMessageQueue) {
            return;
        }
        if (this._sseRestMessageProgress === false) {
            const entry = this._sseMessageQueue.shift();
            if (entry) {
                this._pushMessageToRestApi(entry);
            } else {
                // no-op
            }
        }
        setTimeout(() => {
            this._handleMessageQueue();
        }, 0);
    }

    /**
     * REST send path applies the same E2EE wrapper as other hub interactions.
     *
     * @param {{message:string, resolve:function, reject:function}} entry
     * @private
     */
    _pushMessageToRestApi(entry) {
        const apiUrl = `${this.httpHost}/api/v1/actors/push/message/from/${this.name}/to/${this.hubInfo.name}`;
        const headers = HubInfo.httpHeaders({
            'Content-Type': 'application/json'
        });
        const requestInit = {
            method: 'POST',
            headers: headers,
            body: entry.message
        };

        this._sseRestMessageProgress = true;

        // Keep hubs independent: avoid global fetch queueing so one hub cannot stall another.
        SecurityAgent.fetch(this.UUID, this.hubInfo.aesKey, apiUrl, requestInit)
            .then(async (response) => {
                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(`Error sending message: ${text} (${response.status})`);
                }
                entry.resolve();
            })
            .catch((error) => {
                Log.error('[EventSourceActor._pushMessageToRestApi] Error sending message:', error.message);
                entry.reject(error instanceof Error ? error : new Error(String(error)));
            })
            .finally(() => {
                this._sseRestMessageProgress = false;
            });
    }
}